import { z } from 'zod';
import { isoDateSchema } from './common.js';
import { projectStatusSchema } from './project.js';

/**
 * Wave 32 — Relatório Executivo.
 *
 * Single endpoint that aggregates across every project the caller has
 * access to, and shapes the result for a CEO-level dashboard: what's
 * the whole business doing, across every obra, right now?
 *
 * This sits above the per-project views (DRE, Fluxo de Caixa, Supplier
 * Scorecard, Curva ABC) and never replaces them — it just folds their
 * existing aggregates up one more level and adds trend + risk shapes
 * that only make sense across the portfolio.
 *
 * Scoping mirrors Wave 17 (carteira): admins see everything, everyone
 * else sees projects where they hold at least one row in
 * `project_members`. Financial numbers are redacted for users whose
 * roles are all client/broker — `financialsRedacted=true` tells the UI
 * to hide the cost KPIs. Quality and schedule numbers stay visible.
 */

// ─────────────────────────────────────────────── period (query params)

export const executivoPeriodSchema = z.object({
  /** ISO date, inclusive. When omitted the server defaults to 12 months back. */
  from: isoDateSchema,
  /** ISO date, inclusive. When omitted the server defaults to today. */
  to: isoDateSchema,
});
export type ExecutivoPeriod = z.infer<typeof executivoPeriodSchema>;

// ─────────────────────────────────────────────────────────── sub-shapes

export const executivoProjectCountSchema = z.object({
  total: z.number().int().min(0),
  planning: z.number().int().min(0),
  inProgress: z.number().int().min(0),
  onHold: z.number().int().min(0),
  completed: z.number().int().min(0),
  archived: z.number().int().min(0),
});
export type ExecutivoProjectCount = z.infer<typeof executivoProjectCountSchema>;

export const executivoFinancialTotalsSchema = z.object({
  budgetCents: z.number().int().min(0),
  committedCents: z.number().int().min(0),
  paidCents: z.number().int().min(0),
  /** committed − paid, floored at 0. */
  pendingCents: z.number().int().min(0),
  receivablesCents: z.number().int().min(0),
});
export type ExecutivoFinancialTotals = z.infer<typeof executivoFinancialTotalsSchema>;

export const executivoQualityTotalsSchema = z.object({
  rncOpen: z.number().int().min(0),
  rncClosed: z.number().int().min(0),
  rncCritical: z.number().int().min(0),
  fvsOpen: z.number().int().min(0),
  /** 0..100. Conformance across all verified FVS items in scope. */
  fvsConformanceRate: z.number().min(0).max(100),
});
export type ExecutivoQualityTotals = z.infer<typeof executivoQualityTotalsSchema>;

export const executivoLaborTotalsSchema = z.object({
  totalCostCents: z.number().int().min(0),
  manDays: z.number().min(0),
});
export type ExecutivoLaborTotals = z.infer<typeof executivoLaborTotalsSchema>;

export const executivoTotalsSchema = z.object({
  projects: executivoProjectCountSchema,
  financial: executivoFinancialTotalsSchema,
  quality: executivoQualityTotalsSchema,
  labor: executivoLaborTotalsSchema,
});
export type ExecutivoTotals = z.infer<typeof executivoTotalsSchema>;

export const executivoMonthlyPointSchema = z.object({
  /** 'YYYY-MM'. */
  month: z.string().regex(/^\d{4}-\d{2}$/),
  paidCents: z.number().int().min(0),
  laborCents: z.number().int().min(0),
});
export type ExecutivoMonthlyPoint = z.infer<typeof executivoMonthlyPointSchema>;

export const executivoTopSupplierSchema = z.object({
  supplierId: z.string(),
  name: z.string(),
  totalCents: z.number().int().min(0),
  poCount: z.number().int().min(0),
  /** 0..100 — share of this supplier against sum of top-N supplier spend. */
  percentOfTotal: z.number().min(0).max(100),
});
export type ExecutivoTopSupplier = z.infer<typeof executivoTopSupplierSchema>;

export const executivoProjectRiskSchema = z.object({
  projectId: z.string(),
  name: z.string(),
  status: projectStatusSchema,
  budgetCents: z.number().int().nullable(),
  committedCents: z.number().int().min(0),
  /**
   * (committed − budget) / budget × 100. Null when no budget set.
   * Positive = over budget; negative = under.
   */
  overBudgetPercent: z.number().nullable(),
  /**
   * planned progress − actual progress, rounded. Null when schedule
   * cannot be computed. Positive = behind schedule.
   */
  scheduleSlipPoints: z.number().int().nullable(),
  openRncCount: z.number().int().min(0),
  openIssuesCount: z.number().int().min(0),
  atRisk: z.boolean(),
});
export type ExecutivoProjectRisk = z.infer<typeof executivoProjectRiskSchema>;

// ──────────────────────────────────────────────────────────── response

export const executivoReportSchema = z.object({
  period: executivoPeriodSchema,
  totals: executivoTotalsSchema,
  /** Last 12 months (or the window `period` spans), oldest → newest. */
  monthlyBurn: z.array(executivoMonthlyPointSchema),
  topSuppliers: z.array(executivoTopSupplierSchema),
  projectRisk: z.array(executivoProjectRiskSchema),
  financialsRedacted: z.boolean(),
  generatedAt: z.string(),
});
export type ExecutivoReport = z.infer<typeof executivoReportSchema>;
